import { useState } from 'react';
import {
  Callout,
  DateInput,
  Empty,
  Field,
  Loading,
  Panel,
  Select,
  TableScroll,
  TextInput,
  scheduledStartFor,
  todayIso,
  useLoad,
} from '@affdice/portal-kit';

import type { PlatformApi } from '../../../client.ts';
import { t } from '../../../messages.ts';
import { formatSettingValue, scheduledChangeLine } from '../../../screens.ts';
import { useAbilities } from '../../abilities.tsx';
import { DirectEditDialog } from '../../DirectEditDialog.tsx';
import { settingValueFromText, settingValueProblem, settingValueText } from '../../direct-edit-model.ts';
import { Failure } from '../../shared.tsx';

type SettingsData = Awaited<ReturnType<PlatformApi['operatorSettings']>>;
type SettingRow = SettingsData['items'][number];

/**
 * The operator's own settings — the level every brand inherits from unless it
 * sets its own — read across tenants (logged per access) and, for a viewer
 * holding the edit capability, changed now (3.9d): the same route and the
 * same "applies from" rule as a brand's setting, without a brand.
 */
export function OperatorSettingsPanel({ api, operatorId }: { api: PlatformApi; operatorId: string }) {
  const { can } = useAbilities();
  const [version, setVersion] = useState(0);
  const [open, setOpen] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const loaded = useLoad(() => api.operatorSettings(operatorId), [operatorId, version]);
  const chosen = loaded.state === 'ready' ? loaded.value.items.find((r) => r.key === open) : undefined;

  return (
    <Panel title={t('edit.settings.title')}>
      {saved ? <Callout fold={false}>{saved}</Callout> : null}
      {loaded.state === 'loading' ? <Loading rows={3} /> : null}
      {loaded.state === 'error' ? <Failure error={loaded.error} /> : null}
      {loaded.state === 'ready' ? (
        loaded.value.items.length === 0 ? (
          <Empty>{t('edit.settings.empty')}</Empty>
        ) : (
          <TableScroll>
            <table>
              <thead>
                <tr>
                  <th>{t('brand.settings.colSetting')}</th>
                  <th className="num">{t('brand.settings.colValue')}</th>
                  {can.edit ? <th className="col-action" /> : null}
                </tr>
              </thead>
              <tbody>
                {loaded.value.items.map((row) => (
                  <tr key={row.key}>
                    <td>
                      <div className="wrap">{row.description}</div>
                      <div className="faint">
                        {row.resolved_from === 'OPERATOR' ? t('edit.settings.setHere') : t('brand.settings.inheritedPlatform')}
                      </div>
                      <code className="setting-key">{row.key}</code>
                    </td>
                    <td className="num">
                      {formatSettingValue(row.key, row.value)}
                      {row.scheduled ? (
                        <div className="faint">
                          {scheduledChangeLine(
                            formatSettingValue(row.key, row.scheduled.value),
                            row.scheduled.effective_from,
                          )}
                        </div>
                      ) : null}
                    </td>
                    {can.edit ? (
                      <td className="num col-action">
                        <button
                          type="button"
                          className="copy"
                          aria-label={t('edit.settings.changeOne', { key: row.key })}
                          onClick={() => { setSaved(null); setOpen(row.key); }}
                        >
                          {t('brand.settings.change')}
                        </button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
        )
      ) : null}
      {chosen && loaded.state === 'ready' ? (
        <SettingEdit
          api={api}
          operatorId={operatorId}
          row={chosen}
          today={todayIso()}
          onClose={() => setOpen(null)}
          onDone={(note) => {
            setOpen(null);
            setSaved(note);
            setVersion((v) => v + 1);
          }}
        />
      ) : null}
    </Panel>
  );
}

function SettingEdit({
  api,
  operatorId,
  row,
  today,
  onClose,
  onDone,
}: {
  api: PlatformApi;
  operatorId: string;
  row: SettingRow;
  /** The reader's own today, on their device. */
  today: string;
  onClose: () => void;
  onDone: (note: string) => void;
}) {
  const [value, setValue] = useState(settingValueText(row.value));
  const [effectiveFrom, setEffectiveFrom] = useState(today);
  // Back to the platform default — immediate only, as for a brand.
  const [clear, setClear] = useState(false);
  const later = effectiveFrom.trim() !== '' && effectiveFrom !== today;

  return (
    <DirectEditDialog
      title={row.key}
      submitLabel={clear ? t('edit.settings.clearSubmit') : t('edit.saveNow')}
      ready={clear ? !later : !settingValueProblem(row.kind, value)}
      run={(reason) =>
        api.changeOperatorSetting(
          operatorId,
          row.key,
          clear ? { clear: true } : { value: settingValueFromText(row.kind, value), ...(later ? { effective_from: scheduledStartFor(effectiveFrom, row.scheduled?.effective_from) } : {}) },
          reason,
        )}
      onClose={onClose}
      onDone={() => onDone(t('brand.settings.savedNow', { key: row.key }))}
    >
      <p>{row.description}</p>
      <div className="form-row">
        <Field label={t('edit.settings.value')}>
          {row.options ? (
            <Select value={value} onChange={setValue} options={row.options.map((o) => ({ value: o, label: o }))} />
          ) : row.kind === 'boolean' ? (
            <Select
              value={value}
              onChange={setValue}
              options={[{ value: 'true', label: 'true' }, { value: 'false', label: 'false' }]}
            />
          ) : (
            <TextInput value={value} onChange={setValue} />
          )}
        </Field>
        <Field label={t('brand.propose.appliesFrom')} hint={t('edit.settings.appliesHint')}>
          <DateInput value={effectiveFrom} onChange={setEffectiveFrom} min={today} today={today} />
        </Field>
      </div>
      {row.resolved_from === 'OPERATOR' ? (
        <label className="checkbox-stack-row">
          <input type="checkbox" checked={clear} onChange={(e) => setClear(e.target.checked)} />
          {' '}{t('edit.settings.clear', { value: settingValueText(row.platform_default) || '—' })}
        </label>
      ) : null}
      {clear && later ? <p className="panel-note">{t('brand.propose.clearBlocked')}</p> : null}
    </DirectEditDialog>
  );
}
